import Suggestions from "./Suggestions/Suggestions";
import { useSearch } from "../../Contexts/SearchContext";
import DOMPurify from "dompurify";
import "./SearchBar.scss";
import PopularSearchTerms from "./PopularSearchTerms/PopularSearchTerms";
import magnifyIcon from "../assets/search-icon.svg";
import closeIcon from "../assets/close-icon.svg";
import PreviousSearchTerms from "./PreviousSearchTerms/PreviousSearchTerms";
import { getPreviousSearches } from "../../../helpers/product_finder";
import { useState } from "react";
import { removeBeforeFromActiveFilters, resetFilters } from "../../../../src/helpers";

const SearchBar = ({
  name,
  postSearchHandler,
  getSuggestionHandler,
  placeholderText,
}) => {
  const { state, dispatch } = useSearch();
  const [previousSearchTerms, setPreviousSearchTerms] = useState(getPreviousSearches());

  const {
    searchTerm,
    showSuggestions,
    suggestions,
    selectedFilters,
    componentElement,
    popularSearchTerms,
    searchBarHasFocus,
  } = state;

  const isMobile = window.innerWidth <= 1024;
  let showStartTypingPromptClass =
    searchBarHasFocus && (!searchTerm || searchTerm.length === 0)
      ? " show-start-typing-prompt"
      : "";
  // handles
  const handleChange = ({ target }) => {
    const { value: rawTerm } = target;
    const sanitizedSearchTerm = DOMPurify.sanitize(rawTerm);
    dispatch({ type: "UPDATE_SEARCH_TERM", payload: sanitizedSearchTerm });
    dispatch({ type: "UPDATE_SEARCH_TERM_HAS_CHANGED", payload: true });
    dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: true });

    if (sanitizedSearchTerm.trim() !== "") {
      dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: true });
    } else {
      dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: false });
    }
    getSuggestionHandler(sanitizedSearchTerm);
  };

  if (isMobile) {
    document.body.style.overflow = showSuggestions ? "hidden" : "auto";
  }

  const handleSubmit = (event, suggestion) => {
    let currentSearchTerm = suggestion || searchTerm;
    event?.preventDefault();
    const url = new URL(window.location.href);
    let currentSearchParams = url.searchParams.toString();

    if (currentSearchTerm && currentSearchTerm !== "null") {
      url.searchParams.set("search", currentSearchTerm);
      updatePreviousSearches(currentSearchTerm);
    } else {
      url.searchParams.delete("search");
    }

    if (currentSearchParams !== url.searchParams.toString()) {
      window.history.pushState({}, "", url.href);
    }

    dispatch({ type: "UPDATE_SEARCH_TERM_HAS_CHANGED", payload: false });

    if (currentSearchTerm === "" && selectedFilters.length > 0) {
      currentSearchTerm = "*";
    }

    const sanitizedSearchTerm = DOMPurify.sanitize(currentSearchTerm);
    postSearchHandler(sanitizedSearchTerm, dispatch);
    componentElement.querySelector("#product-finder-search-input")?.blur();
    dispatch({ type: "UPDATE_SHOW_RESULTS", payload: true });
  };

  const capitalize = (str) =>
    str
      .toLowerCase()
      .split(" ")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(" ")
      .split("-")
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join("-");

  const handleSuggestionOrPopularSearchTerm = (selection) => {
    const searchField = document.getElementById("product-finder-search-input");
    const capitalizedSelection = capitalize(selection);
    if (searchField) {
      searchField.value = capitalizedSelection;
      searchField.blur();
    }

    dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: false });
    dispatch({ type: "UPDATE_SEARCH_TERM", payload: capitalizedSelection });
    dispatch({ type: "UPDATE_SEARCH_TERM_HAS_CHANGED", payload: true });

    handleSubmit(null, capitalizedSelection);
  };

  const handleSearchBarClose = () => {
    if (searchTerm !== "") {
      dispatch({ type: "UPDATE_SEARCH_TERM", payload: "" });
      dispatch({ type: "UPDATE_SEARCH_BAR_HAS_FOCUS", payload: false });
      dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: false });
      dispatch({ type: "UPDATE_SEARCH_PARAMS", payload: "" });
      dispatch({ type: "UPDATE_SELECTED_FILTERS", payload: [] });
      

      const url = new URL(window.location.href);
      url.searchParams.delete("search");
      window.history.pushState({}, "", url);
    } else {
      removeBeforeFromActiveFilters();
      resetFilters(componentElement);
      dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: false });
      dispatch({ type: "UPDATE_SELECTED_FILTERS", payload: [] });
      return;
    }
  };

  const updatePreviousSearches = (term) => {
    if (term && !previousSearchTerms.includes(term)) {
      const updatedTerms = [...previousSearchTerms, term];
      if (updatedTerms.length > 5) updatedTerms.shift();
      setPreviousSearchTerms(updatedTerms);
      sessionStorage.setItem("pfPreviousSearches", JSON.stringify(updatedTerms));
    }
  };

  return (
    <div className={`cmp-product-finder__search-bar-section ${showStartTypingPromptClass}`}>
      <form className="form-group" onSubmit={handleSubmit}>
        <div className="cmp-product-finder__search-bar-input-container" style={{ borderBottom: "none" }}>
          <input
            autoComplete="off"
            className="form-control"
            id="product-finder-search-input"
            name={name}
            value={searchTerm}
            placeholder={placeholderText}
            type="text"
            onChange={handleChange}
            onFocus={() => {
              dispatch({ type: "UPDATE_SEARCH_BAR_HAS_FOCUS", payload: true });
            }}
            onBlur={() => {
              setTimeout(() => {
                dispatch({ type: "UPDATE_SHOW_SUGGESTIONS", payload: false });
                dispatch({ type: "UPDATE_SEARCH_BAR_HAS_FOCUS", payload: false });
              }, 200); // give time for clicks to register
            }}
          />

          {isMobile ? (
            <>
              <button
                type="button"
                className="chi close-icon"
                tabIndex="0"
                role="button"
                aria-label="close search suggestions"
                onClick={handleSearchBarClose}
                onTouchStart={handleSearchBarClose}
              >
                <img src={closeIcon} alt="close Icon" />
              </button>

              <span
                className={`chi search-icon magnify ${searchBarHasFocus ? "magnify-focused" : ""}`}
                onClick={handleSubmit}
                onTouchStart={handleSubmit}
              >
                <img src={magnifyIcon} alt="Search Icon" />
              </span>
            </>
          ) : (
            <>
              <span
                className="close-icon-focused"
                onClick={handleSearchBarClose}
                onTouchStart={handleSearchBarClose}
              >
                <img
                  src={closeIcon}
                  alt="close Icon"
                  className="close-icon-img"
                />
              </span>

              <span
                className={`chi search-icon magnify ${searchBarHasFocus ? "magnify-focused" : ""}`}
                onClick={handleSubmit}
              >
                <img src={magnifyIcon} alt="Search Icon" />
              </span>
            </>
          )}
          <span className="start-typing-text">Start typing</span>
        </div>
        {searchBarHasFocus && (
          <div className="search-suggestions-container">
            {previousSearchTerms && (
              <PreviousSearchTerms
                previousSearchTerms={previousSearchTerms}
                handlePreviousSearchTerm={handleSuggestionOrPopularSearchTerm}
              />
            )}
            {popularSearchTerms && (
              <PopularSearchTerms
                popularSearchTerms={popularSearchTerms}
                handlePopularSearchTerm={handleSuggestionOrPopularSearchTerm}
              />
            )}
            {searchTerm && (
              <Suggestions
                searchTerm={searchTerm}
                suggestions={suggestions}
                handleSuggestion={handleSuggestionOrPopularSearchTerm}
              />
            )}
          </div>
        )}
      </form>
    </div>
  );
};

export default SearchBar;
